import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import EmojiPicker from 'emoji-picker-react';
import EditorToolbar from './EditorToolbar';
import BlockComponent from './BlockComponent';
import { useBlocks } from '../hooks/useBlocks';
import { pageApi } from '../services/api';
import { BLOCK_TYPES } from '../utils/blockTypes';
import { generateSlug } from '../utils/slugUtils';
import {
  FaSave,
  FaTimes,
  FaImage,
  FaUpload,
  FaSmile,
  FaImage as FaCoverImage,
  FaTrash,
  FaSpinner,
  FaEye,
  FaLink,
  FaCheck,
  FaCrop,
  FaArrowsAltH,
  FaArrowsAltV,
} from 'react-icons/fa';
import { uploadApi } from '../services/api';
import '../styles/PageEditor.css';

const PageEditor = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const historyId = searchParams.get('historyId');
  const navigate = useNavigate();

  const [page, setPage] = useState(null);
  const [title, setTitle] = useState('');
  const [originalTitle, setOriginalTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [copied, setCopied] = useState(false);
  const [coverImage, setCoverImage] = useState('');
  const [pageIcon, setPageIcon] = useState('');
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingIcon, setUploadingIcon] = useState(false);

  // Simplified crop functionality states
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [originalImage, setOriginalImage] = useState(null);
  const [cropData, setCropData] = useState({
    x: 0,
    y: 0,
    width: 300,
    height: 169,
    aspectRatio: 16 / 9,
    isDragging: false,
    dragType: null,
    displayOffsetX: 0,
    displayOffsetY: 0,
    displayWidth: 0,
    displayHeight: 0
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [canvasRef, setCanvasRef] = useState(null);
  const [previewCanvasRef, setPreviewCanvasRef] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });

  const {
    blocks,
    loading,
    error,
    selectedBlockId,
    setSelectedBlockId,
    pageId,
    addBlock,
    updateBlock,
    deleteBlock,
    addImageBlock,
    addFileBlock,
    saveAllBlocks,
    loadBlocks,
    moveBlock,
    setBlocks
  } = useBlocks(slug, historyId);

  const blocksContainerRef = useRef(null);
  const iconPickerRef = useRef(null);
  const coverImageInputRef = useRef(null);
  const imageRef = useRef(null);
  const cropContainerRef = useRef(null);

  useEffect(() => {
    const loadPage = async () => {
      try {
        const result = await pageApi.getBySlug(slug, historyId);

        if (!result || !result.data) {
          throw new Error('Page not found');
        }

        setPage(result.data);
        const pageTitle = result.data?.page?.title || 'Untitled';
        setTitle(pageTitle);
        setOriginalTitle(pageTitle);
        setCoverImage(result.data?.page?.cover_image || '');
        setPageIcon(result.data?.page?.icon || '');

        await loadBlocks();
      } catch (error) {
        console.error('Failed to load page:', error);
        alert(`Failed to load page: ${error.message}`);
        navigate('/');
      }
    };

    if (slug) {
      loadPage();
    }
  }, [slug, historyId, navigate, loadBlocks]);

  const handleSave = async () => {
    if (!pageId) return;

    setSaving(true);
    try {
      const updateData = {
        title,
        cover_image: coverImage,
        icon: pageIcon
      };

      await pageApi.update(pageId, updateData);

      await saveAllBlocks();

      // Update the original title to match saved title
      setOriginalTitle(title);

      // Update URL slug if title has changed
      if (title !== originalTitle) {
        const newSlug = generateSlug(title);
        if (newSlug && newSlug !== slug) {
          // Navigate to the new slug without reloading
          const queryParams = historyId ? `?historyId=${historyId}` : '';
          navigate(`/page/edit/${newSlug}${queryParams}`, { replace: true });
        }
      }

      // Trigger pages refresh in sidebar
      window.dispatchEvent(new Event('pagesUpdated'));
    } catch (error) {
      console.error('Failed to save:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCoverImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    // Show crop modal
    const reader = new FileReader();
    reader.onload = (e) => {
      setOriginalImage(e.target.result);
      setSelectedFile(file);
      setCropModalOpen(true);

      // Reset crop data
      setCropData({
        x: 50,
        y: 50,
        width: 300,
        height: 169,
        aspectRatio: 16 / 9,
        isDragging: false,
        dragType: null
      });
    };
    reader.readAsDataURL(file);

    event.target.value = '';
  };

  // Handle image load
  const handleImageLoad = () => {
    if (cropContainerRef.current && imageRef.current) {
      const containerRect = cropContainerRef.current.getBoundingClientRect();
      const img = imageRef.current;

      // Calculate the actual displayed image dimensions (object-fit: contain)
      const containerWidth = containerRect.width;
      const containerHeight = containerRect.height;
      const imgAspect = img.naturalWidth / img.naturalHeight;
      const containerAspect = containerWidth / containerHeight;

      let displayWidth, displayHeight, offsetX, offsetY;

      if (imgAspect > containerAspect) {
        // Image is wider than container - fit to width
        displayWidth = containerWidth;
        displayHeight = containerWidth / imgAspect;
        offsetX = 0;
        offsetY = (containerHeight - displayHeight) / 2;
      } else {
        // Image is taller than container - fit to height
        displayHeight = containerHeight;
        displayWidth = containerHeight * imgAspect;
        offsetX = (containerWidth - displayWidth) / 2;
        offsetY = 0;
      }

      // Set initial crop area (80% of display area, 16:9 ratio)
      const cropWidth = Math.min(displayWidth * 0.8, displayWidth);
      const cropHeight = cropWidth / (16 / 9);

      // Ensure crop doesn't exceed display bounds
      const finalCropWidth = cropWidth;
      const finalCropHeight = Math.min(cropHeight, displayHeight);

      setImageLoaded(true);
      setCropData({
        x: offsetX + (displayWidth - finalCropWidth) / 2,
        y: offsetY + (displayHeight - finalCropHeight) / 2,
        width: finalCropWidth,
        height: finalCropHeight,
        aspectRatio: 16 / 9,
        isDragging: false,
        dragType: null,
        displayOffsetX: offsetX,
        displayOffsetY: offsetY,
        displayWidth: displayWidth,
        displayHeight: displayHeight
      });
    }
  };

  // Mouse handlers for cropping
  const handleMouseDown = (e) => {
    const rect = cropContainerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Check if click is on resize handle
    const handles = [
      { type: 'nw', x: cropData.x - 10, y: cropData.y - 10, width: 20, height: 20 },
      { type: 'ne', x: cropData.x + cropData.width - 10, y: cropData.y - 10, width: 20, height: 20 },
      { type: 'sw', x: cropData.x - 10, y: cropData.y + cropData.height - 10, width: 20, height: 20 },
      { type: 'se', x: cropData.x + cropData.width - 10, y: cropData.y + cropData.height - 10, width: 20, height: 20 },
    ];

    for (const handle of handles) {
      if (x >= handle.x && x <= handle.x + handle.width &&
        y >= handle.y && y <= handle.y + handle.height) {
        setCropData(prev => ({ ...prev, isDragging: true, dragType: handle.type }));
        return;
      }
    }

    // Check if click is inside crop area (for moving)
    if (x >= cropData.x && x <= cropData.x + cropData.width &&
      y >= cropData.y && y <= cropData.y + cropData.height) {
      setCropData(prev => ({ ...prev, isDragging: true, dragType: 'move' }));
    }
  };

  const handleMouseMove = (e) => {
    if (!cropData.isDragging || !cropContainerRef.current) return;

    const rect = cropContainerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Get display bounds from cropData
    const displayWidth = cropData.displayWidth;
    const displayHeight = cropData.displayHeight;
    const displayOffsetX = cropData.displayOffsetX;
    const displayOffsetY = cropData.displayOffsetY;

    const maxX = displayOffsetX + displayWidth;
    const maxY = displayOffsetY + displayHeight;
    const MIN_SIZE = 5;

    switch (cropData.dragType) {
      case 'move':
        setCropData(prev => ({
          ...prev,
          x: Math.max(displayOffsetX, Math.min(x - prev.width / 2, maxX - prev.width)),
          y: Math.max(displayOffsetY, Math.min(y - prev.height / 2, maxY - prev.height))
        }));
        break;

      case 'se':
        setCropData(prev => ({
          ...prev,
          width: Math.max(MIN_SIZE, Math.min(x - prev.x, maxX - prev.x)),
          height: Math.max(MIN_SIZE, Math.min(y - prev.y, maxY - prev.y))
        }));
        break;

      case 'sw':
        setCropData(prev => {
          const newWidth = Math.max(MIN_SIZE, Math.min(prev.x + prev.width - x, maxX - prev.x));
          const newX = prev.x + prev.width - newWidth;
          return {
            ...prev,
            x: Math.max(displayOffsetX, newX),
            width: newWidth,
            height: prev.height
          };
        });
        break;

      case 'ne':
        setCropData(prev => {
          const newHeight = Math.max(MIN_SIZE, Math.min(prev.y + prev.height - y, maxY - prev.y));
          const newY = prev.y + prev.height - newHeight;
          return {
            ...prev,
            y: Math.max(displayOffsetY, newY),
            width: prev.width,
            height: newHeight
          };
        });
        break;

      case 'nw':
        setCropData(prev => {
          const newWidth = Math.max(MIN_SIZE, Math.min(prev.x + prev.width - x, maxX - prev.x));
          const newHeight = Math.max(MIN_SIZE, Math.min(prev.y + prev.height - y, maxY - prev.y));
          const newX = prev.x + prev.width - newWidth;
          const newY = prev.y + prev.height - newHeight;
          return {
            ...prev,
            x: Math.max(displayOffsetX, newX),
            y: Math.max(displayOffsetY, newY),
            width: newWidth,
            height: newHeight
          };
        });
        break;

      default:
        break;
    }
  };

  const handleMouseUp = () => {
    setCropData(prev => ({ ...prev, isDragging: false, dragType: null }));
  };

  // Crop and upload function
  const handleCropComplete = async () => {
    if (!imageRef.current || !cropData.width || !cropData.height) {
      alert('Please select a crop area');
      return;
    }

    setUploadingCover(true);
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      const img = imageRef.current;
      const originalWidth = img.naturalWidth;
      const originalHeight = img.naturalHeight;

      // Get the crop area relative to the display
      const cropDisplayX = cropData.x;
      const cropDisplayY = cropData.y;
      const cropDisplayWidth = cropData.width;
      const cropDisplayHeight = cropData.height;

      // Get the actual displayed image dimensions from cropData
      const displayWidth = cropData.displayWidth;
      const displayHeight = cropData.displayHeight;
      const displayOffsetX = cropData.displayOffsetX;
      const displayOffsetY = cropData.displayOffsetY;

      // Calculate the crop area relative to the displayed image (not container)
      const relativeX = (cropDisplayX - displayOffsetX) / displayWidth;
      const relativeY = (cropDisplayY - displayOffsetY) / displayHeight;
      const relativeWidth = cropDisplayWidth / displayWidth;
      const relativeHeight = cropDisplayHeight / displayHeight;

      // Convert to actual image coordinates
      const actualCropX = relativeX * originalWidth;
      const actualCropY = relativeY * originalHeight;
      const actualCropWidth = relativeWidth * originalWidth;
      const actualCropHeight = relativeHeight * originalHeight;

      // Validate crop bounds
      if (actualCropX < 0 || actualCropY < 0 ||
        actualCropX + actualCropWidth > originalWidth ||
        actualCropY + actualCropHeight > originalHeight) {
        throw new Error('Crop area is outside image bounds');
      }

      // Set canvas to crop dimensions
      canvas.width = actualCropWidth;
      canvas.height = actualCropHeight;

      // Draw the cropped image
      ctx.drawImage(
        img,
        actualCropX,
        actualCropY,
        actualCropWidth,
        actualCropHeight,
        0,
        0,
        actualCropWidth,
        actualCropHeight
      );

      // Convert canvas to blob
      canvas.toBlob(async (blob) => {
        if (!blob) {
          throw new Error('Failed to create cropped image');
        }

        // Create file from blob
        const fileExt = selectedFile.name.split('.').pop();
        const croppedFile = new File([blob], `cropped_${Date.now()}.${fileExt}`, {
          type: blob.type
        });

        // Upload cropped image
        const response = await uploadApi.uploadFile(croppedFile, 'image');
        const fileUrl = response.data.data.url;
        setCoverImage(fileUrl);

        // Close modal and cleanup
        setCropModalOpen(false);
        setOriginalImage(null);
        setSelectedFile(null);
        setUploadingCover(false);

      }, 'image/jpeg', 0.95);

    } catch (error) {
      console.error('Failed to crop image:', error);
      alert('Failed to crop image. Please try again.');
      setUploadingCover(false);
    }
  };

  // Change aspect ratio
  const handleAspectRatioChange = (ratio) => {
    if (!cropContainerRef.current || !imageLoaded) return;

    const aspectValue = ratio[0] / ratio[1];
    const displayWidth = cropData.displayWidth;
    const displayHeight = cropData.displayHeight;
    const displayOffsetX = cropData.displayOffsetX;
    const displayOffsetY = cropData.displayOffsetY;

    let newWidth = displayWidth * 0.7;
    let newHeight = newWidth / aspectValue;

    // Ensure crop area stays within display bounds
    if (newHeight > displayHeight) {
      newHeight = displayHeight * 0.7;
      newWidth = newHeight * aspectValue;
    }

    // Center the crop area
    const newX = displayOffsetX + (displayWidth - newWidth) / 2;
    const newY = displayOffsetY + (displayHeight - newHeight) / 2;

    setCropData(prev => ({
      ...prev,
      aspectRatio: aspectValue,
      x: Math.max(displayOffsetX, newX),
      y: Math.max(displayOffsetY, newY),
      width: Math.max(5, newWidth),
      height: Math.max(5, newHeight)
    }));
  };

  // Cancel cropping
  const handleCropCancel = () => {
    setCropModalOpen(false);
    setOriginalImage(null);
    setSelectedFile(null);
  };

  // Add mouse event listeners for cropping
  useEffect(() => {
    if (cropModalOpen) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [cropModalOpen, cropData.isDragging]);

  const handleIconSelect = (emojiData) => {
    setPageIcon(emojiData.emoji);
    setShowIconPicker(false);
  };

  const handleIconUpload = async (event) => {
    const file = event.target.files[0];
    event.target.value = ''; // Reset input immediately so same file can be selected again

    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 1 * 1024 * 1024) {
      alert('Icon size should be less than 1MB');
      return;
    }

    setUploadingIcon(true);
    try {
      const response = await uploadApi.uploadFile(file, 'image');
      const fileUrl = response.data.data.url;
      setPageIcon(fileUrl);
    } catch (error) {
      console.error('Failed to upload icon:', error);
      alert('Failed to upload icon');
    } finally {
      setUploadingIcon(false);
      event.target.value = '';
    }
  };

  const handleRemoveCover = () => {
    setCoverImage('');
  };

  const handleRemoveIcon = () => {
    setPageIcon('');
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (iconPickerRef.current && !iconPickerRef.current.contains(event.target)) {
        setShowIconPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleAddBlock = (type, content = '', properties = {}, position = 'below', relativeToIndex = -1) => {
    let newBlockId;

    if (relativeToIndex >= 0 && position === 'below') {
      newBlockId = addBlock(type, content, properties);
    } else if (relativeToIndex >= 0 && position === 'above') {
      newBlockId = addBlock(type, content, properties);
    } else {
      // newBlockId = addBlock(type, content, properties);
      newBlockId = addBlock(type, content, properties, position);
    }


    if (newBlockId) {
      setTimeout(() => {
        const newBlockElement = document.querySelector(`[data-block-id="${newBlockId}"]`);
        if (newBlockElement) {
          const editableElement = newBlockElement.querySelector('[contenteditable="true"]') ||
            newBlockElement.querySelector('input, textarea');
          if (editableElement) {
            editableElement.focus();
          }
        }
        handleSave();
      }, 100);
    }
    return newBlockId;
  };

  const handleUpdateBlock = (blockId, updates) => {
    updateBlock(blockId, updates);
  };

  const handleDeleteBlock = (blockId) => {
    const currentIndex = blocks.findIndex(b => b.id === blockId);
    const isLastBlock = currentIndex === blocks.length - 1;

    deleteBlock(blockId);

    if (blocks.length > 1) {
      if (isLastBlock && currentIndex > 0) {
        setSelectedBlockId(blocks[currentIndex - 1].id);
      } else if (!isLastBlock && currentIndex < blocks.length - 1) {
        setSelectedBlockId(blocks[currentIndex + 1]?.id || blocks[currentIndex - 1]?.id);
      } else if (currentIndex === 0 && blocks.length > 1) {
        setSelectedBlockId(blocks[1]?.id);
      }
    } else {
      setSelectedBlockId(null);
    }
  };

  const handleAddBlockAtIndex = (index, type = BLOCK_TYPES.TEXT) => {
    // Get the reference block
    const referenceBlock = blocks[index];
    if (!referenceBlock) return handleAddBlock(type);

    // Calculate new order_index (add 1 to reference block's order_index)
    const newOrderIndex = referenceBlock.order_index + 1;

    // Update order_index for all subsequent blocks
    const updatedBlocks = blocks.map((block, idx) => {
      if (block.order_index >= newOrderIndex) {
        return {
          ...block,
          order_index: block.order_index + 1
        };
      }
      return block;
    });

    // Create new block
    const newBlockId = crypto.randomUUID();
    const newBlock = {
      id: newBlockId,
      type,
      properties: {
        title: [['']]
      },
      format: {},
      order_index: newOrderIndex,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      parent_id: pageId || null
    };

    // Insert new block at correct position
    updatedBlocks.splice(index + 1, 0, newBlock);

    // Re-index blocks
    const finalBlocks = updatedBlocks.map((block, idx) => ({
      ...block,
      order_index: idx
    }));

    setBlocks(finalBlocks);
    setSelectedBlockId(newBlockId);

    return newBlockId;
  };

  const handleFormatText = (formatType) => {
    if (selectedBlockId) {
      const block = blocks.find(b => b.id === selectedBlockId);
      if (block) {
        const currentProperties = block.properties || {};
        updateBlock(selectedBlockId, {
          properties: {
            ...currentProperties,
            [formatType]: true
          }
        });
      }
    }
  };

  const handleUploadFile = async (fileUrl, fileName, fileSize) => {
    const newBlockId = addFileBlock(fileUrl, fileName, fileSize);
    setSelectedBlockId(newBlockId);

    // Auto-save after adding file block
    setTimeout(() => {
      handleSave();
    }, 500); // Small delay to ensure block is properly added
  };

  const handleUploadImage = async (imageUrl, caption = '') => {
    const newBlockId = addImageBlock(imageUrl, caption);
    setSelectedBlockId(newBlockId);

    // Auto-save after adding image block
    setTimeout(() => {
      handleSave();
    }, 500); // Small delay to ensure block is properly added
  };

  const handleMoveBlock = useCallback((fromIndex, toIndex) => {
    if (fromIndex === toIndex ||
      fromIndex < 0 ||
      fromIndex >= blocks.length ||
      toIndex < 0 ||
      toIndex >= blocks.length) {
      return;
    }

    if (moveBlock) {
      moveBlock(fromIndex, toIndex);
    } else {
      setBlocks(prev => {
        const newBlocks = [...prev];
        const [movedBlock] = newBlocks.splice(fromIndex, 1);
        newBlocks.splice(toIndex, 0, movedBlock);

        return newBlocks.map((block, idx) => ({
          ...block,
          order_index: idx
        }));
      });
    }

    setDragOverIndex(toIndex);
  }, [moveBlock, setBlocks, blocks.length]);

  const handleDragStart = useCallback(() => {
    setIsDragging(true);
    setDragOverIndex(null);
  }, []);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    setDragOverIndex(null);
  }, []);

  const handleKeyDown = useCallback((e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 's') {
      e.preventDefault();
      handleSave();
    }

    if (e.key === 'Escape') {
      if (selectedBlockId) {
        setSelectedBlockId(null);
        e.preventDefault();
      } else if (window.confirm('Discard changes?')) {
        navigate(`/page/${slug}`);
      }
    }

    if (e.key === 'Enter' && !e.shiftKey && selectedBlockId) {
      const blockIndex = blocks.findIndex(b => b.id === selectedBlockId);
      if (blockIndex !== -1) {
        e.preventDefault();
        handleAddBlockAtIndex(blockIndex);
      }
    }

    if (e.key === 'Delete' && selectedBlockId) {
      e.preventDefault();
      handleDeleteBlock(selectedBlockId);
    }

    if (selectedBlockId && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      const currentIndex = blocks.findIndex(b => b.id === selectedBlockId);

      if (e.key === 'ArrowUp' && currentIndex > 0) {
        e.preventDefault();
        const prevBlockId = blocks[currentIndex - 1].id;
        setSelectedBlockId(prevBlockId);

        setTimeout(() => {
          const prevBlockElement = document.querySelector(`[data-block-id="${prevBlockId}"]`);
          if (prevBlockElement) {
            const editableElement = prevBlockElement.querySelector('[contenteditable="true"]') ||
              prevBlockElement.querySelector('input, textarea');
            if (editableElement) {
              editableElement.focus();
            }
          }
        }, 10);
      }

      if (e.key === 'ArrowDown' && currentIndex < blocks.length - 1) {
        e.preventDefault();
        const nextBlockId = blocks[currentIndex + 1].id;
        setSelectedBlockId(nextBlockId);

        setTimeout(() => {
          const nextBlockElement = document.querySelector(`[data-block-id="${nextBlockId}"]`);
          if (nextBlockElement) {
            const editableElement = nextBlockElement.querySelector('[contenteditable="true"]') ||
              nextBlockElement.querySelector('input, textarea');
            if (editableElement) {
              editableElement.focus();
            }
          }
        }, 10);
      }
    }

    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && !selectedBlockId) {
      e.preventDefault();
      handleAddBlock(BLOCK_TYPES.TEXT);
    }
  }, [handleSave, handleAddBlock, handleAddBlockAtIndex, handleDeleteBlock, selectedBlockId, slug, navigate, blocks, setSelectedBlockId]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.block-component') && !e.target.closest('.editor-toolbar') && !e.target.closest('.quick-actions')) {
        setSelectedBlockId(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [setSelectedBlockId]);

  const handleSelectBlock = (blockId, index) => {
    setSelectedBlockId(blockId);

    setTimeout(() => {
      const blockElement = document.querySelector(`[data-block-id="${blockId}"]`);
      if (blockElement) {
        blockElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest'
        });
      }
    }, 50);
  };

  const getListNumberForBlock = useCallback((blockIndex, listGroup) => {
    if (!blocks || blockIndex < 0 || blockIndex >= blocks.length) return 1;

    let listNumber = 1;
    let currentIndex = blockIndex - 1;

    if (listGroup) {
      while (currentIndex >= 0) {
        const prevBlock = blocks[currentIndex];
        if (prevBlock?.type === BLOCK_TYPES.NUMBERED_LIST &&
          prevBlock.properties?.listGroup === listGroup) {
          listNumber++;
          currentIndex--;
        } else {
          break;
        }
      }
    } else {
      while (currentIndex >= 0) {
        if (blocks[currentIndex]?.type === BLOCK_TYPES.NUMBERED_LIST) {
          listNumber++;
          currentIndex--;
        } else {
          break;
        }
      }
    }

    return listNumber;
  }, [blocks]);

  if (error) {
    return (
      <div className="error-container">
        <h2>Error Loading Editor</h2>
        <p>{error.message || error}</p>
        <p>Page Slug: {slug}</p>
        <button
          onClick={() => navigate('/')}
          className="btn-primary"
        >
          Go Home
        </button>
      </div>
    );
  }

  if (!page && !loading) {
    return (
      <div className="error-container">
        <h2>Page Not Found</h2>
        <p>Unable to load page with slug: {slug}</p>
        <button
          onClick={() => navigate('/')}
          className="btn-primary"
        >
          Go Home
        </button>
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="page-editor-container">
        {/* Crop Modal */}
        {cropModalOpen && originalImage && (
          <div className="crop-modal-overlay">
            <div className="crop-modal">
              <div className="crop-modal-header">
                <h3>Crop Cover Image</h3>
                <button
                  className="close-modal-btn"
                  onClick={handleCropCancel}
                  disabled={uploadingCover}
                >
                  ×
                </button>
              </div>

              <div className="crop-modal-content">
                <div className="crop-instructions">
                  <p>Drag the crop box to select the area you want to keep.</p>
                  <p>Use the handles at the corners to resize.</p>
                </div>

                <div
                  className="crop-container"
                  ref={cropContainerRef}
                  onMouseDown={handleMouseDown}
                  style={{
                    width: '100%',
                    height: '400px',
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: cropData.isDragging ? 'grabbing' : 'default'
                  }}
                >
                  <img
                    ref={imageRef}
                    src={originalImage}
                    alt="Crop preview"
                    onLoad={handleImageLoad}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                  />

                  {/* Crop overlay */}
                  {imageLoaded && (
                    <div
                      className="crop-overlay"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)'
                      }}
                    >
                      {/* Crop area */}
                      <div
                        className="crop-area"
                        style={{
                          position: 'absolute',
                          left: cropData.x,
                          top: cropData.y,
                          width: cropData.width,
                          height: cropData.height,
                          border: '2px dashed #fff',
                          boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.7)',
                          cursor: 'move'
                        }}
                      >
                        {/* Resize handles */}
                        <div
                          className="crop-handle nw"
                          style={{
                            position: 'absolute',
                            top: -8,
                            left: -8,
                            width: 16,
                            height: 16,
                            backgroundColor: '#fff',
                            border: '2px solid #007bff',
                            borderRadius: '50%',
                            cursor: 'nw-resize'
                          }}
                        />
                        <div
                          className="crop-handle ne"
                          style={{
                            position: 'absolute',
                            top: -8,
                            right: -8,
                            width: 16,
                            height: 16,
                            backgroundColor: '#fff',
                            border: '2px solid #007bff',
                            borderRadius: '50%',
                            cursor: 'ne-resize'
                          }}
                        />
                        <div
                          className="crop-handle sw"
                          style={{
                            position: 'absolute',
                            bottom: -8,
                            left: -8,
                            width: 16,
                            height: 16,
                            backgroundColor: '#fff',
                            border: '2px solid #007bff',
                            borderRadius: '50%',
                            cursor: 'sw-resize'
                          }}
                        />
                        <div
                          className="crop-handle se"
                          style={{
                            position: 'absolute',
                            bottom: -8,
                            right: -8,
                            width: 16,
                            height: 16,
                            backgroundColor: '#fff',
                            border: '2px solid #007bff',
                            borderRadius: '50%',
                            cursor: 'se-resize'
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="crop-controls">
                  <div className="aspect-ratio-buttons">
                    <p>Aspect Ratio:</p>
                    <div className="ratio-buttons">
                      <button
                        onClick={() => handleAspectRatioChange([16, 9])}
                        className={`ratio-btn ${cropData.aspectRatio === 16 / 9 ? 'active' : ''}`}
                      >
                        16:9 (Widescreen)
                      </button>
                      <button
                        onClick={() => handleAspectRatioChange([16, 4])}
                        className={`ratio-btn ${cropData.aspectRatio === 16 / 4 ? 'active' : ''}`}
                      >
                        16:2 (Wide)
                      </button>
                      <button
                        onClick={() => handleAspectRatioChange([4, 3])}
                        className={`ratio-btn ${cropData.aspectRatio === 4 / 3 ? 'active' : ''}`}
                      >
                        4:3 (Standard)
                      </button>
                      <button
                        onClick={() => handleAspectRatioChange([1, 1])}
                        className={`ratio-btn ${cropData.aspectRatio === 1 ? 'active' : ''}`}
                      >
                        1:1 (Square)
                      </button>
                      <button
                        onClick={() => setCropData(prev => ({ ...prev, aspectRatio: null }))}
                        className={`ratio-btn ${!cropData.aspectRatio ? 'active' : ''}`}
                      >
                        Free
                      </button>
                    </div>
                  </div>

                  <div className="crop-dimensions">
                    <p>Dimensions: {Math.round(cropData.width)} × {Math.round(cropData.height)}</p>
                    <p>Position: {Math.round(cropData.x)}, {Math.round(cropData.y)}</p>
                  </div>
                </div>

                <div className="crop-modal-actions">
                  <button
                    className="btn-secondary"
                    onClick={handleCropCancel}
                    disabled={uploadingCover}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-primary"
                    onClick={handleCropComplete}
                    disabled={uploadingCover}
                  >
                    {uploadingCover ? (
                      <>
                        <FaSpinner className="spinner" />
                        Processing...
                      </>
                    ) : (
                      'Crop & Save'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {coverImage ? (
          <div className="page-cover">
            <img src={coverImage} alt="Page cover" />
            {uploadingCover && (
              <div className="uploading-indicator">
                <FaSpinner className="uploading-spinner" />
              </div>
            )}
            <div className="cover-actions">
              <button
                onClick={() => coverImageInputRef.current?.click()}
                className="cover-action-btn cover-upload-btn"
                title="Change cover"
                disabled={uploadingCover}
              >
                <FaUpload size={14} />
              </button>
              {/* <button
                onClick={() => {
                  // Open crop modal with current image
                  setOriginalImage(coverImage);
                  setCropModalOpen(true);
                }}
                className="cover-action-btn cover-crop-btn"
                title="Crop cover"
                disabled={uploadingCover || !coverImage}
              >
                <FaCrop size={14} />
              </button> */}
              <button
                onClick={handleRemoveCover}
                className="cover-action-btn"
                title="Remove cover"
                disabled={uploadingCover}
              >
                <FaTrash size={14} />
              </button>
            </div>
          </div>
        ) : (
          <div
            className="page-cover placeholder"
            onClick={() => coverImageInputRef.current?.click()}
          >
            <div className="cover-placeholder">
              <FaCoverImage size={32} />
              <span>Add cover image</span>
            </div>
          </div>
        )}

        <input
          type="file"
          ref={coverImageInputRef}
          className="cover-upload-input"
          accept="image/*"
          onChange={handleCoverImageUpload}
        />

        <div className="editor-header">
          <div className="editor-header-content">
            <div className="page-icon-section">
              <div
                className="page-icon-container"
                onClick={() => !uploadingIcon && setShowIconPicker(!showIconPicker)}
              >
                <div className="page-icon">
                  {pageIcon ? (
                    pageIcon.startsWith('data:') || pageIcon.startsWith('http') || pageIcon.startsWith('blob:') ? (
                      <img src={pageIcon} alt="Page icon" />
                    ) : (
                      <span className="page-icon-emoji">{pageIcon}</span>
                    )
                  ) : (
                    <span className="page-icon-placeholder">✨</span>
                  )}

                  {uploadingIcon && (
                    <div className="uploading-indicator">
                      <FaSpinner className="uploading-spinner" />
                    </div>
                  )}

                  {pageIcon && (
                    <div className="icon-actions">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveIcon();
                        }}
                        className="icon-action-btn"
                        title="Remove icon"
                        disabled={uploadingIcon}
                      >
                        <FaTrash size={10} />
                      </button>
                    </div>
                  )}
                </div>
                <div className="icon-options">
                  <button
                    onClick={() => document.getElementById('icon-upload-input').click()}
                    className="icon-option-btn"
                    disabled={uploadingIcon}
                  >
                    <FaUpload /> Upload
                  </button>
                </div>
              </div>
              <div className="editor-actions">
                <button
                  className="btn-secondary"
                  onClick={() => {
                    if (window.confirm('Discard changes?')) {
                      navigate(`/page/${slug}`);
                    }
                  }}
                  disabled={saving}
                >
                  <FaTimes /> Cancel
                </button>
                <button
                  className="btn-view"
                  onClick={() => navigate(`/page/${slug}`)}
                >
                  <FaEye /> View
                </button>
                <button
                  className="btn-copy-url"
                  onClick={() => {
                    const publicUrl = `${window.location.origin}/page/${slug}`;
                    navigator.clipboard.writeText(publicUrl)
                      .then(() => {
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      })
                      .catch(err => {
                        console.error('Failed to copy URL: ', err);
                        const textArea = document.createElement('textarea');
                        textArea.value = publicUrl;
                        document.body.appendChild(textArea);
                        textArea.select();
                        document.execCommand('copy');
                        document.body.removeChild(textArea);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      });
                  }}
                  disabled={saving}
                >
                  {copied ? <FaCheck /> : <FaLink />}
                  {copied ? 'Copied!' : 'Copy URL'}
                </button>
                <button
                  className="btn-primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  <FaSave /> {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>

            {showIconPicker && (
              <>
                <div
                  className="emoji-picker-overlay"
                  onClick={() => setShowIconPicker(false)}
                />
                <div className="icon-picker-container" ref={iconPickerRef}>
                  <EmojiPicker
                    onEmojiClick={handleIconSelect}
                    width={300}
                    height={400}
                  />
                </div>
              </>
            )}

            <input
              id="icon-upload-input"
              type="file"
              className="icon-upload-input"
              accept="image/*"
              onChange={handleIconUpload}
            />

            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={`editor-page-title ${title !== originalTitle ? 'modified' : ''}`}
              placeholder="Untitled"
              autoFocus
            />

          </div>
        </div>

        <EditorToolbar
          onAddBlock={(type, content = '', properties = {}) => {
            handleAddBlock(type, content, properties);
          }}
          onFormatText={handleFormatText}
          onUploadImage={handleUploadImage}
          onUploadFile={handleUploadFile}
          selectedBlockId={selectedBlockId}
          selectedBlockType={blocks.find(b => b.id === selectedBlockId)?.type}
          blocksCount={blocks.length}
          selectedBlockIndex={blocks.findIndex(b => b.id === selectedBlockId)}
        />

        <div className="blocks-container" ref={blocksContainerRef}>
          {!blocks || blocks.length === 0 ? (
            <div className="empty-editor">
              <div className="empty-icon">📝</div>
              <h3>Start writing</h3>
              <p>This page is empty. Add your first block to get started.</p>
              <div className="empty-actions">
                <button
                  onClick={() => handleAddBlock(BLOCK_TYPES.TEXT)}
                  className="empty-action-btn"
                >
                  Add Text Block
                </button>
                <button
                  onClick={() => handleAddBlock(BLOCK_TYPES.HEADER)}
                  className="empty-action-btn"
                >
                  Add Header
                </button>
              </div>
            </div>
          ) : (
            blocks.map((block, index) => {
              if (!block) return null;

              return (
                <BlockComponent
                  key={block.id || `block-${index}`}
                  block={block}
                  isSelected={block.id === selectedBlockId}
                  onUpdate={(updates) => handleUpdateBlock(block.id, updates)}
                  onDelete={() => handleDeleteBlock(block.id)}
                  onSelect={() => handleSelectBlock(block.id, index)}
                  onAddBelow={() => handleAddBlockAtIndex(index)}
                  onAddAbove={() => handleAddBlock(BLOCK_TYPES.TEXT, '', {}, 'above', index)}
                  onMoveBlock={handleMoveBlock}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  onGetListNumber={() => getListNumberForBlock(index, block.properties?.listGroup)}
                  dragOverIndex={dragOverIndex}
                  isDragging={isDragging}
                  placeholder={index === 0 ? "Start typing your content here..." : "Type something..."}
                  isFirst={index === 0}
                  isLast={index === blocks.length - 1}
                  index={index}
                  totalBlocks={blocks.length}
                  isReadOnly={false}
                />
              );
            })
          )}
        </div>
      </div>
    </DndProvider>
  );
};

export default PageEditor;